import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser, FaLock, FaEnvelope, FaEye, FaEyeSlash,
  FaUsers, FaWallet, FaNetworkWired, FaFileContract, FaTimes,
  FaShieldAlt, FaChartLine, FaArrowRight
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import Alert from "./Alert";
import { useAuth } from "../context/AuthContext";

const AuthPage = () => {
  const {
    formData, mode, setMode, setFormData,
    handleSubmitRegister, alert, showAlert,
    handleSubmitLogin,
  } = useAuth();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [termsOpen, setTermsOpen] = useState(false);

  useEffect(() => {
    if (mode !== "signup") return;
    const refFromUrl = searchParams.get("ref");
    if (refFromUrl) {
      setFormData((prev) => ({ ...prev, ReferralCode: refFromUrl.trim() }));
    }
  }, [mode, searchParams, setFormData]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setTermsOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mode === "signup") {
      if (formData.password !== formData.confirmPassword) return showAlert("error", "Passwords do not match!");
      if (formData.password.length < 8) return showAlert("error", "Minimum 8 characters required!");
      if (!formData.termsAccepted) return showAlert("error", "Accept terms to continue!");
      handleSubmitRegister(e);
    } else {
      if (!formData.email || !formData.password) return showAlert("error", "All fields required!");
      handleSubmitLogin(e);
    }
  };

  const togglePassword = (type) => {
    const key = type === "password" ? "showPassword" : "showConfirmPassword";
    setFormData(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
<div className="min-h-screen bg-[#07111F] text-white font-sans relative overflow-hidden flex items-center justify-center">      
      {/* Soft Background Accents for login */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-700/20 blur-[140px] rounded-full -translate-y-1/2 translate-x-1/2" />
<div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-yellow-400/10 blur-[140px] rounded-full translate-y-1/2 -translate-x-1/2" />
      <Alert 
        type={alert.type} message={alert.message} isOpen={alert.isOpen} 
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })} 
      />

     <div className="relative z-10 w-full max-w-6xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* LEFT: CONTENT & INFO */}
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} className="hidden lg:block space-y-8">
       <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900/30 text-yellow-400 text-[10px] font-black uppercase tracking-widest border border-yellow-400/20">
    Secure Infrastructure
</div>
         <h1 className="text-6xl font-black tracking-tight leading-[0.9]">
    Manage your <br />
    <span className="text-yellow-400">Assets.</span>
</h1>
       <p className="text-slate-300 text-lg max-w-md">
    Join thousands of users utilizing the 3Workers ecosystem for automated returns and secure wallet management.
</p>
          
          <div className="space-y-4 pt-4">
            <InfoRow icon={FaShieldAlt} title="End-to-End Security" desc="AES-256 encryption for all transaction data." />
            <InfoRow icon={FaChartLine} title="Real-time Analytics" desc="Track your ROI with precision and clarity." />
          </div>
        </motion.div>

        {/* RIGHT: AUTH FORM CARD */}
       <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="bg-[#0B1D34] border border-blue-800/40 rounded-[2.5rem] p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,.55)] backdrop-blur-xl"
>
          <div className="mb-10 text-center">
             <h2 className="text-3xl font-black tracking-tight uppercase">
               {mode === 'login' ? 'Welcome Back' : 'Create Node'}
             </h2>
<p className="text-slate-300 text-sm mt-1">
    Access the 3Workers Dashboard
</p>          </div>

          {/* Toggle Switch */}
<div className="flex bg-[#071523] rounded-2xl p-1.5 mb-8 border border-blue-800/40">            <button 
              onClick={() => setMode("login")}
             className={`flex-1 py-3 rounded-xl text-xs font-black transition-all ${
mode === "login"
? "bg-yellow-400 text-[#07111F] shadow-lg"
: "text-slate-300 hover:text-white"
}`}
            > LOGIN </button>
            <button 
              onClick={() => setMode("signup")}
              className={`flex-1 py-3 rounded-xl text-xs font-black transition-all ${mode === 'signup' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}
            > SIGN UP </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {mode === 'signup' && (
              <InputGroup label="Full Name" name="name" icon={FaUser} placeholder="Enter your name" value={formData.name} onChange={handleInputChange} />
            )}

            <InputGroup label="Email Address" name="email" icon={FaEnvelope} placeholder="name@domain.com" value={formData.email} onChange={handleInputChange} type="email" />

            {mode === 'signup' && (
              <InputGroup label="Wallet Address (ERC20 Must)" name="walletAddress" icon={FaWallet} placeholder="0x..." value={formData.walletAddress} onChange={handleInputChange} />
            )}

            {/* Main Password Field */}
<div className="relative group">              <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Password</label>
              <div className="relative group">
             <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300 group-focus-within:text-yellow-400 transition-colors">
    <FaLock size={14}/>
</div>
       <input
  type={formData.showPassword ? "text" : "password"}
  name="password"
  value={formData.password}
  onChange={handleInputChange}
  placeholder="••••••••"
  required
  className="w-full pl-11 pr-12 py-4 bg-[#071523] border border-blue-800/40 rounded-2xl text-white placeholder:text-slate-500 text-sm focus:bg-[#102744] focus:border-yellow-400 focus:ring-4 focus:ring-yellow-400/20 outline-none transition-all"
/>
                <button type="button" onClick={() => togglePassword("password")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-indigo-600 transition-colors">
                  {formData.showPassword ? <FaEyeSlash size={16}/> : <FaEye size={16}/>}
                </button>
              </div>
            </div>

            {/* Confirm Password Field (Signup only) */}
            {mode === 'signup' && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Confirm Password</label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors"><FaLock size={14}/></div>
                  <input 
                    type={formData.showConfirmPassword ? "text" : "password"} name="confirmPassword" 
                    value={formData.confirmPassword} onChange={handleInputChange}
                   className="w-full pl-11 pr-12 py-4 bg-[#071523] border border-blue-800/40 rounded-2xl text-sm text-white placeholder:text-slate-500 focus:bg-[#102744] focus:border-yellow-400 focus:ring-4 focus:ring-yellow-400/20 outline-none transition-all"
                    placeholder="••••••••" required={mode === 'signup'}
                  />
                  <button type="button" onClick={() => togglePassword("confirmPassword")} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-indigo-600 transition-colors">
                    {formData.showConfirmPassword ? <FaEyeSlash size={16}/> : <FaEye size={16}/>}
                  </button>
                </div>
              </div>
            )}

            {mode === 'signup' && (
              <>
                <InputGroup label="Referral (Optional)" name="ReferralCode" icon={FaUsers} placeholder="Code" value={formData.ReferralCode} onChange={handleInputChange} />
                <div className="flex items-start gap-3 p-1">
                  <input 
                    type="checkbox" name="termsAccepted" checked={formData.termsAccepted} onChange={handleCheckboxChange}
                    className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
<p className="text-[11px] text-slate-300 font-medium leading-tight">                    I agree to the <button
type="button"
onClick={() => setTermsOpen(true)}
className="text-yellow-400 font-bold hover:underline">
Terms of Service
</button> and Privacy Policy.
                  </p>
                </div>
              </>
            )}

            {mode === 'login' && (
              <div className="flex justify-end">
             <button
type="button"
onClick={() => navigate("/forget-password")}
className="text-[11px] font-black text-yellow-400 hover:text-yellow-300 uppercase tracking-tighter">
Forgot Password?
</button>
              </div>
            )}

          <motion.button
whileHover={{ y: -2 }}
whileTap={{ scale: 0.98 }}
className="w-full py-4 bg-yellow-400 text-[#07111F] rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-yellow-500/20 hover:bg-yellow-300 transition-all flex items-center justify-center gap-2"
>
              {mode === 'login' ? 'Sign In' : 'Initialize Account'} <FaArrowRight size={12}/>
            </motion.button>
          </form>
        </motion.div>
      </div>

      {/* TERMS MODAL */}
  {/* TERMS MODAL */}
<AnimatePresence>
  {termsOpen && (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setTermsOpen(false)} className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" />
   <motion.div
initial={{ opacity: 0, scale: .9, y:20 }}
animate={{ opacity:1, scale:1, y:0 }}
exit={{ opacity:0, scale:.9, y:20 }}
className="relative w-full max-w-2xl bg-[#0B1D34] border border-blue-800/40 rounded-[2.5rem] shadow-2xl overflow-hidden"
>
        {/* Header */}
   <div className="px-6 py-5 md:px-8 md:py-6 border-b border-blue-800/40 flex items-center justify-between bg-[#071523]">
          <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-yellow-400 text-[#07111F] flex items-center justify-center">
    <FaFileContract size={18}/>
</div>
            <div>
              <h3 className="font-black text-slate-800 uppercase tracking-tighter text-sm md:text-base">Terms & Conditions</h3>
              <p className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">Last Updated: April 2026</p>
            </div>
          </div>
          <button onClick={() => setTermsOpen(false)} className="text-slate-400 hover:text-red-500 transition-colors"><FaTimes/></button>
        </div>

        {/* Content */}
   <div className="p-6 md:p-8 max-h-[65vh] overflow-y-auto text-sm text-slate-300 leading-relaxed font-medium space-y-8 scrollbar-thin scrollbar-thumb-blue-700">
          <section>
            <h4 className="text-indigo-600 font-black mb-3 uppercase text-[11px] tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></span> 1. Nature of the Platform
            </h4>
            <p className="pl-4 border-l-2 border-slate-100 italic">3Workers Labs is a private, invite-only digital asset management and blockchain research platform. By accessing this platform, you acknowledge participating in a private peer-to-peer (P2P) network. This is not a banking institution or a public financial offering.</p>
          </section>

          <section>
            <h4 className="text-indigo-600 font-black mb-3 uppercase text-[11px] tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></span> 2. Risk Disclosure
            </h4>
            <div className="pl-4 space-y-2">
              <p><strong>Volatility:</strong> Digital assets (USDT/XMR/BTC) are subject to extreme market volatility.</p>
              <p><strong>No Advice:</strong> Content is for educational purposes only. It does not constitute financial or legal advice.</p>
              <p><strong>Total Risk:</strong> Only participate with capital you can afford to lose. Past performance (25% monthly) is not a guarantee of future results.</p>
            </div>
          </section>

<section className="bg-blue-900/20 p-5 rounded-2xl border border-yellow-400/20">            <h4 className="text-indigo-700 font-black mb-3 uppercase text-[11px] tracking-[0.2em] flex items-center gap-2">
              <FaShieldAlt className="text-indigo-500"/> 3. The "4x Capping" & Reinvestment
            </h4>
            <ul className="space-y-3 pl-4 list-disc marker:text-indigo-400 text-slate-700">
              <li><strong>Earnings Cap:</strong> Every participation slot is capped at 400% (4x) of the initial contribution.</li>
              <li><strong>Account Freezing:</strong> Once reached, withdrawals are paused until reactivation.</li>
              <li><strong>Reactivation (+1):</strong> Must contribute a new "Activation Unit" equivalent to or greater than the original amount.</li>
            </ul>
          </section>

          <section>
            <h4 className="text-indigo-600 font-black mb-3 uppercase text-[11px] tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></span> 4. Network & Distribution
            </h4>
            <p className="pl-4">Bonuses are distributed across 10 levels, capped at 20%. <strong>Anti-Spam:</strong> Fake multi-accounts or self-referral loops result in immediate termination without refund.</p>
          </section>

          <section>
            <h4 className="text-indigo-600 font-black mb-3 uppercase text-[11px] tracking-[0.2em] flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></span> 5. Withdrawal Policy
            </h4>
            <p className="pl-4">Processed within 24-72 hours according to the liquidity cycle. Gas fees are the responsibility of the user and deducted from the amount.</p>
          </section>

<section className="bg-[#071523] text-slate-300 p-5 rounded-2xl">            <h4 className="text-white font-black mb-2 uppercase text-[10px] tracking-[0.3em]">Final Compliance</h4>
            <p className="text-xs leading-relaxed">3Workers Labs reserves the right to modify these terms, bonus structures, or capping rules at any time to ensure ecosystem sustainability. Compliance with local jurisdiction laws is the user's sole responsibility.</p>
          </section>
        </div>

        {/* Footer */}
  <div className="p-6 md:p-8 border-t border-blue-800/40 flex flex-col md:flex-row justify-end gap-3 bg-[#071523]">
          <button onClick={() => setTermsOpen(false)} className="px-6 py-3 rounded-xl font-bold text-slate-400 hover:text-slate-600 transition-colors uppercase text-[10px] tracking-widest order-2 md:order-1">Dismiss</button>
          <button 
            onClick={() => { setFormData(p => ({ ...p, termsAccepted: true })); setTermsOpen(false); }} 
       className="px-10 py-4 bg-yellow-400 text-[#07111F] rounded-xl font-black uppercase text-[10px] tracking-[0.2em] shadow-xl shadow-yellow-500/20 hover:bg-yellow-300 transition-all"
          >
            Agree & Initialize Node
          </button>
        </div>
      </motion.div>
    </div>
  )}
</AnimatePresence>
    </div>
  );
};

// Helper Components
const InputGroup = ({ label, icon: Icon, ...props }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-black uppercase text-slate-300 ml-1">
      {label}
    </label>

    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-blue-300 group-focus-within:text-yellow-400 transition-colors">
        <Icon size={14} />
      </div>

      <input
className="w-full pl-11 pr-12 py-4 bg-[#071523] border border-blue-800/40 rounded-2xl text-sm text-white placeholder:text-slate-500 focus:bg-[#102744] focus:border-yellow-400 focus:ring-4 focus:ring-yellow-400/20 outline-none transition-all"        {...props}
      />
    </div>
  </div>
);

const InfoRow = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-4 items-start">
<div className="w-10 h-10 rounded-xl bg-[#102744] border border-blue-800 flex items-center justify-center text-yellow-400 shrink-0"> <Icon size={16}/></div>
    <div>
      <h4 className="font-black text-slate-800 text-sm uppercase tracking-tighter">{title}</h4>
      <p className="text-slate-400 text-xs mt-0.5">{desc}</p>
    </div>
  </div>
);

export default AuthPage;